/**
 * Carousel node editor — sibling of slot-node-editor but renders ALL items
 * in the referenced group as slides with dot-nav. preset applies uniformly
 * to every slide.
 */

import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { ExternalLink, Pencil, Repeat, Trash2 } from 'lucide-react';
import type {
  CarouselNode,
  GroupContentSummary,
  HydratedGroupItem,
  SlotPreset,
} from '@news-cms/shared';
import { SLOT_PRESETS } from '@news-cms/shared';
import {
  groupContentPreviewQueryOptions,
  groupContentsListQueryOptions,
} from '@/lib/group-contents';
import { ItemSlot } from '@/components/homepage/layout-preview';
import { SlotGroupEditor } from '@/components/homepage/slot-group-editor';
import { useEditor } from './editor-context';
import { NodeToolbar } from './node-toolbar';

export function CarouselNodeEditor({ node }: { node: CarouselNode }) {
  const { selectedId, select, updateCarousel, remove } = useEditor();
  const isSelected = selectedId === node.id;

  const groupsQuery = useQuery(groupContentsListQueryOptions({ page: 1, pageSize: 100 }));
  const groups: GroupContentSummary[] = groupsQuery.data?.items ?? [];
  const selectedGroup = groups.find((g) => g.slug === node.groupSlug) ?? null;

  // Preview items — all of them render as slides (1 per slide).
  const previewQuery = useQuery({
    ...groupContentPreviewQueryOptions(selectedGroup?.id ?? ''),
    enabled: !!selectedGroup,
  });
  const items: HydratedGroupItem[] = previewQuery.data ?? [];

  // Defensive: legacy carousel-containers migrated by the server may leave
  // preset undefined when read by the admin editor (which doesn't apply
  // the same legacy transform). Fall back to 'card' so the preview still
  // renders something coherent.
  const effectivePreset = node.preset ?? 'card';

  const [activeIdx, setActiveIdx] = useState(0);
  const safeIdx = items.length === 0 ? 0 : Math.min(activeIdx, items.length - 1);
  const activeItem = items[safeIdx];

  // Edit modal state — opened by the bubble-menu pencil button.
  const [editOpen, setEditOpen] = useState(false);

  return (
    <div
      onClick={(e) => {
        e.stopPropagation();
        select(node.id);
      }}
      className={`relative rounded border px-px pb-0.5 pt-2 transition ${
        isSelected
          ? 'border-indigo-400 bg-indigo-50/40 dark:border-indigo-500 dark:bg-indigo-950/20'
          : 'border-indigo-200 bg-indigo-50/30 dark:border-indigo-900/40 dark:bg-indigo-950/10'
      }`}
    >
      <div className="mb-1.5 flex items-center gap-1.5 text-[10px] uppercase tracking-wide text-indigo-500">
        <Repeat className="h-3 w-3" />
        Carousel · {selectedGroup?.name ?? '(ยังไม่ได้เลือก group)'}
        <span className="rounded bg-indigo-100 px-1.5 text-[9px] text-indigo-600 dark:bg-indigo-900/50 dark:text-indigo-300">
          {effectivePreset}
        </span>
      </div>

      <NodeToolbar
        visible={isSelected}
        extra={
          <div className="mr-1 flex items-center gap-1 border-r border-slate-200 pr-2 dark:border-slate-700">
            <select
              value={node.groupSlug}
              onChange={(e) => updateCarousel(node.id, { groupSlug: e.target.value })}
              onClick={(e) => e.stopPropagation()}
              title="เลือก Group Content"
              className="border-input bg-background h-7 max-w-[220px] rounded border px-1.5 text-xs"
            >
              <option value="">— เลือก group —</option>
              {groups.map((g) => (
                <option key={g.id} value={g.slug}>
                  {g.name}
                </option>
              ))}
            </select>
            <select
              value={effectivePreset}
              onChange={(e) => updateCarousel(node.id, { preset: e.target.value as SlotPreset })}
              onClick={(e) => e.stopPropagation()}
              title="Preset (รูปแบบการแสดงทุก slide)"
              className="border-input bg-background h-7 w-[100px] rounded border px-1.5 text-xs"
            >
              {SLOT_PRESETS.map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </select>
            {selectedGroup && (
              <Link
                to="/group-contents/$id/edit"
                params={{ id: selectedGroup.id }}
                title="แก้ Group (tab ใหม่)"
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="border-input rounded border p-1 text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800"
              >
                <ExternalLink className="h-3 w-3" />
              </Link>
            )}
          </div>
        }
        buttons={[
          {
            key: 'edit',
            label: 'แก้ไข',
            icon: <Pencil className="h-3.5 w-3.5" />,
            onClick: () => setEditOpen(true),
            disabled: !selectedGroup,
          },
          {
            key: 'delete',
            label: 'ลบ',
            icon: <Trash2 className="h-3.5 w-3.5" />,
            onClick: () => remove(node.id),
            variant: 'destructive',
          },
        ]}
      />

      <div className="space-y-2">
        {!node.groupSlug && (
          <div className="rounded border border-dashed border-indigo-300 px-2 py-3 text-center text-xs text-indigo-400 dark:border-indigo-900/50">
            ยังไม่ได้เลือก Group Content — คลิกที่ carousel นี้แล้วเลือกจาก toolbar ด้านบน
          </div>
        )}

        {!selectedGroup && node.groupSlug && (
          <div className="rounded border border-amber-300 bg-amber-50 px-2 py-1 text-xs text-amber-800 dark:border-amber-900/60 dark:bg-amber-950/30 dark:text-amber-300">
            ⚠ group "<code>{node.groupSlug}</code>" ไม่พบ
          </div>
        )}

        {selectedGroup && (
          <div onClick={(e) => e.stopPropagation()}>
            {/* Carousel preview — one ItemSlot rendered at a time with
                dot-nav showing all slides. Mirrors public renderer pattern. */}
            {previewQuery.isLoading ? (
              <div className="rounded border border-dashed border-indigo-200 px-2 py-1 text-center text-[10px] text-indigo-400 dark:border-indigo-900/50">
                กำลังโหลด slides…
              </div>
            ) : items.length === 0 ? (
              <div className="rounded border border-dashed border-amber-300 bg-amber-50 px-2 py-1 text-center text-[10px] text-amber-700 dark:border-amber-900/60 dark:bg-amber-950/30 dark:text-amber-300">
                Group ยังไม่มี item — คลิก ✏️ แก้ไข เพื่อเพิ่ม slide
              </div>
            ) : (
              <div className="rounded border border-dashed border-indigo-300 bg-indigo-50/40 p-1.5 dark:border-indigo-800/60 dark:bg-indigo-950/20">
                <ItemSlot item={activeItem} variant={effectivePreset} size="hero" />
                <div className="mt-2 flex items-center justify-center gap-1.5">
                  {items.map((it, i) => (
                    <button
                      key={`${it.type}-${i}`}
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setActiveIdx(i);
                      }}
                      title={`Slide ${i + 1}`}
                      className={`h-2 rounded-full transition ${
                        i === safeIdx
                          ? 'w-4 bg-indigo-500'
                          : 'w-2 bg-indigo-200 hover:bg-indigo-400 dark:bg-indigo-800 dark:hover:bg-indigo-600'
                      }`}
                    />
                  ))}
                </div>
                <div className="mt-1 text-center text-[10px] text-indigo-500/70">
                  {safeIdx + 1} / {items.length}
                </div>
              </div>
            )}
            {/* Edit modal (embedded — controlled by the bubble-menu pencil) */}
            <SlotGroupEditor
              groupId={selectedGroup.id}
              layoutTemplateId={null}
              embedded={{ open: editOpen, onOpenChange: setEditOpen }}
            />
          </div>
        )}
      </div>
    </div>
  );
}
